// Pashmak's voice for any Persian text: pre-recorded lesson/letter audio first,
// then server TTS (cached in memory and in the browser's Cache Storage, so a
// phrase is only ever generated once per device). One thing plays at a time.

import { useSyncExternalStore } from "react";
import { postBlob } from "./api";

const BUNDLED = `${import.meta.env.BASE_URL}content/audio/`;
const CACHE = "pashmak-tts-v1";
const memory = new Map<string, string>(); // key → object URL

let current: HTMLAudioElement | null = null;
let playing: string | null = null; // text currently playing
let loadingText: string | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());

/** Anything else that makes sound (voice calls, story player) claims the speaker. */
const exclusives = new Set<() => void>();
export function registerExclusive(stop: () => void) {
  exclusives.add(stop);
  return () => void exclusives.delete(stop);
}
export function claimAudio(except?: () => void) {
  exclusives.forEach((f) => f !== except && f());
  if (except !== stopSpeech) stopSpeech();
}

let clips: Promise<Set<string>> | null = null;
/** Hashes of the pre-recorded clips shipped with the app (content/audio.json). */
function bundledClips(): Promise<Set<string>> {
  clips ??= fetch(`${import.meta.env.BASE_URL}content/audio.json`)
    .then((r) => (r.ok ? r.json() : []))
    .then((list: string[]) => new Set(list))
    .catch(() => {
      clips = null; // retry next time
      return new Set<string>();
    });
  return clips;
}

export async function sha1(s: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export interface SpeakOptions {
  speed?: number;
  /** Romanization: tells the voice how to pronounce (Persian omits short vowels). */
  hint?: string | null;
  /** Story character voice. */
  speaker?: string | null;
  /** Pre-recorded audio key (lesson items, letters). */
  bundledKey?: string | null;
  /** "Hear it again": restart instead of toggling off. */
  replay?: boolean;
}

/** Get a playable URL for this text (fetching + caching TTS if needed). */
export async function audioUrl(text: string, o: SpeakOptions = {}): Promise<string> {
  const t = text.trim();
  if (o.bundledKey) {
    const h = await sha1(o.bundledKey);
    if ((await bundledClips()).has(h)) return `${BUNDLED}path-${h}.mp3`;
  }
  const speed = Math.round((o.speed ?? 0.9) * 100) / 100;
  const key = `tts|${speed}|${o.speaker || ""}|${o.hint || ""}|${t}`;
  const hit = memory.get(key);
  if (hit) return hit;
  const cacheKey = `${BUNDLED}__tts/${await sha1(key)}`;
  let blob: Blob | null = null;
  try {
    const c = await caches.open(CACHE);
    const r = await c.match(cacheKey);
    if (r) blob = await r.blob();
  } catch {
    /* Cache Storage unavailable (private mode) */
  }
  if (!blob) {
    blob = await postBlob("/tts", { text: t, speed, hint: o.hint || undefined, speaker: o.speaker || undefined });
    try {
      const c = await caches.open(CACHE);
      await c.put(cacheKey, new Response(blob, { headers: { "content-type": "audio/mpeg" } }));
    } catch {
      /* ignore */
    }
  }
  const url = URL.createObjectURL(blob);
  memory.set(key, url);
  return url;
}

export function stopSpeech() {
  if (current) {
    current.pause();
    current.src = "";
    current = null;
  }
  if (playing || loadingText) {
    playing = null;
    loadingText = null;
    emit();
  }
}

/** Speak Persian text. Tapping the same phrase again stops it (unless `replay`). */
export async function speak(text: string, o: SpeakOptions = {}): Promise<void> {
  const t = text.trim();
  if (!t) return;
  if (playing === t && current) {
    if (!o.replay) return stopSpeech();
    current.currentTime = 0;
    void current.play();
    return;
  }
  if (o.replay && loadingText === t) return;
  claimAudio(stopSpeech);
  stopSpeech();
  loadingText = t;
  emit();
  try {
    const url = await audioUrl(t, o);
    if (loadingText !== t) return; // something else started meanwhile
    const a = new Audio(url);
    current = a;
    loadingText = null;
    playing = t;
    emit();
    a.onended = a.onerror = () => {
      if (current === a) {
        current = null;
        playing = null;
        emit();
      }
    };
    await a.play();
  } catch (e) {
    if (loadingText === t) loadingText = null;
    if (playing === t) playing = null;
    emit();
    throw e;
  }
}

/** Which text is playing / loading (for speaker-button states). */
export function useSpeech(): { playing: string | null; loading: string | null } {
  const snap = useSyncExternalStore(
    (f) => (listeners.add(f), () => void listeners.delete(f)),
    () => `${playing ?? ""}\u0000${loadingText ?? ""}`
  );
  const [p, l] = snap.split("\u0000");
  return { playing: p || null, loading: l || null };
}
